import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RefreshIcon from '@mui/icons-material/Refresh';
import RestoreIcon from '@mui/icons-material/Restore';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as authApi from '../../services/authApi';
import * as quoteApi from './quoteApi';
import SchematicViewer from './SchematicViewer';
import { ImportSkip, LineImprovements, Quote, QuoteLine, SortDir, displayTagRef, sortBy } from './quoteApi';

const tableHeadCell = { whiteSpace: 'nowrap' as const };
const rectButtonSx = {
  borderRadius: '4px',
  textTransform: 'none',
  fontWeight: 700,
  letterSpacing: 0,
  fontSize: '0.8125rem',
  lineHeight: 1.4,
  padding: '6px 14px',
  minWidth: 0,
  whiteSpace: 'nowrap',
  boxShadow: 'none',
};

const NAMING = /^(?:CABLE ASSY|CONNECTOR|ENCLOSURE|MAIN BODY|CONTACTOR|CABLE AS|ASSEMBLY|TERMINAL|HARNESS|FITTING|BRACKET|SWITCH|OPTION|BUSBAR|CABLE|SCREW|CRIMP|LABEL|RELAY|GLAND|CONN|LOOM|FUSE|ASSY|MCB|KIT|PLC|PSU|DRIVE|INVERTER|VSD): (?:(?!\bREV\b)(?!\bDRG\b)(?!DRG[A-Z0-9])(?!REV[A-Z0-9])[A-Z0-9².\- ])+(?: REV [A-Z0-9\-\/.]+)?(?: DRG [A-Z0-9\-\/.]+)?$/;
// A typed suggestion can use any uppercase type, such as ACCESSORY or GROMMET.
const TYPE_DETAILS = /^[A-Z][A-Z0-9 /&-]{0,40}: \S.*$/;

const originColor = (origin: string) => {
  if (origin === 'Valid') return 'success';
  if (origin === 'Additional' || origin === 'Untagged') return 'warning';
  return 'default';
};

const enrichColor = (status: string) => {
  if (status === 'complete') return 'success';
  if (status === 'crawling') return 'info';
  if (status === 'stale' || status === 'new_erp' || status === 'failed') return 'warning';
  return 'default';
};

const enrichLabel = (status: string) => {
  switch (status) {
    case 'new_erp':
      return 'New item';
    case 'stale':
      return 'Stale';
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

const COMPLIANCE_OPTIONS = [
  { value: 'Unknown', label: 'Unknown' },
  { value: 'Compliant', label: 'Compliant' },
  { value: 'Not_x0020_Compliant', label: 'Not Compliant' },
  { value: 'Not_x0020_Applicable', label: 'Not Applicable' },
];

const REPLENISHMENT_OPTIONS = ['Purchase', 'Prod. Order'];
const UNIT_OPTIONS = ['EA', 'MTR', 'LTR'];

const optionLabel = (row: quoteApi.ErpCodeOption) => (
  row.name && row.name !== row.code ? `${row.code} · ${row.name}` : row.code
);

const countryLabel = (code: string) => {
  try {
    const name = new Intl.DisplayNames(['en'], { type: 'region' }).of(code);
    return name && name !== code ? `${code} · ${name}` : code;
  } catch {
    return code;
  }
};

const createProblems = (preview: quoteApi.ErpPreview): string[] => {
  const errors: string[] = [];
  const limited = (label: string, value: string, max: number, required: boolean) => {
    const text = (value || '').trim();
    if (required && !text) errors.push(`${label} is required.`);
    else if (text.length > max) errors.push(`${label} is ${text.length} characters. The maximum is ${max}.`);
  };
  limited('Vendor item no.', preview.vendor_item_no, 50, true);
  limited('Description', preview.description, 50, true);
  limited('Description 2', preview.description2, 50, false);
  limited('Customer part no.', preview.customerPartNumber || '', 20, false);
  limited('UL file no.', preview.ulFileNo, 10, false);
  limited('UL category no.', preview.ulCcnNo, 12, false);
  if ((preview.ulFileNo || '').trim().toUpperCase() === 'NONE') errors.push('UL file no. cannot be NONE. Leave it blank.');
  if ((preview.ulCcnNo || '').trim().toUpperCase() === 'NONE') errors.push('UL category no. cannot be NONE. Leave it blank.');
  if (!REPLENISHMENT_OPTIONS.includes(preview.replenishmentSystem)) errors.push('Replenishment system must be Purchase or Prod. Order.');
  if (!UNIT_OPTIONS.includes(preview.baseUnitOfMeasureCode)) errors.push('Base unit of measure must be EA, MTR or LTR.');
  if (preview.unitCost == null || Number.isNaN(Number(preview.unitCost)) || Number(preview.unitCost) < 0) {
    errors.push('Unit cost must be a number zero or greater.');
  }
  ([['RoHS', preview.rohsCompliant], ['REACH', preview.reachCompliance], ['Conflict minerals', preview.conflictMineralsStatus]] as const)
    .forEach(([label, value]) => {
      if (!COMPLIANCE_OPTIONS.some((option) => option.value === value)) errors.push(`${label} must be one of the ERP options.`);
    });
  const member = (value: string, options: quoteApi.ErpCodeOption[] | undefined, label: string) => {
    const text = (value || '').trim();
    if (!text) return;
    if (!options || !options.length) {
      errors.push(`${label} list did not load, so it cannot be checked.`);
      return;
    }
    if (!options.some((row) => row.code === text)) errors.push(`${label} is not in the ERP list.`);
  };
  if (!(preview.manufacturerCode || '').trim()) errors.push('Choose a manufacturer from the ERP list.');
  else member(preview.manufacturerCode, preview.manufacturers, 'Manufacturer');
  member(preview.itemCategoryCode, preview.itemCategories, 'Item category');
  member(preview.commodityCode, preview.commodities, 'Commodity code');
  member(preview.costCenter, preview.costCenters, 'Cost centre');
  member(preview.countryOfOriginCode, preview.countries, 'Country of origin');
  return errors;
};

const FIELD_LABEL: Record<string, string> = {
  manufacturer: 'Manufacturer',
  description: 'Description',
  customer_part_number: 'Customer No.',
  rohs: 'RoHS',
  reach: 'REACH',
  ul_file: 'UL file',
  ul_ccn: 'UL CCN',
};

const DESCRIPTION_MAX = 50;

const ManualDescriptionField: React.FC<{
  locked: boolean;
  busy: boolean;
  onChange: (value: string) => void;
  onApprove: (value: string) => void;
  onReject: () => void;
}> = ({ locked, busy, onChange, onApprove, onReject }) => {
  const [text, setText] = useState('');
  const trimmed = text.trim();
  const ready = trimmed.length > 0 && trimmed.length <= DESCRIPTION_MAX;
  const patternOk = TYPE_DETAILS.test(trimmed);
  const helper = !trimmed
    ? `0 / ${DESCRIPTION_MAX}. Type the description, then Approve.`
    : patternOk
      ? `${trimmed.length} / ${DESCRIPTION_MAX}`
      : `${trimmed.length} / ${DESCRIPTION_MAX}. Approve is available. Prefer TYPE: DETAILS, for example FUSE: 10A 2 POLE`;
  return (
    <>
      <TextField
        size="small"
        value={text}
        disabled={locked}
        helperText={helper}
        inputProps={{ maxLength: DESCRIPTION_MAX }}
        onChange={(event) => {
          setText(event.target.value);
          onChange(event.target.value);
        }}
        sx={{ minWidth: 260 }}
      />
      <Button
        size="small"
        variant={ready ? 'contained' : 'outlined'}
        color={ready ? 'warning' : 'inherit'}
        disabled={locked || busy || !ready}
        sx={rectButtonSx}
        onClick={() => onApprove(trimmed)}
      >
        Approve
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        disabled={locked || busy}
        sx={rectButtonSx}
        onClick={onReject}
      >
        Reject
      </Button>
    </>
  );
};

const OUTCOME_LABEL: Record<string, string> = {
  add: 'Add',
  amend: 'Amend',
  append: 'Append',
  flag: 'Flag',
};

const ImprovementPanel: React.FC<{
  improvements?: LineImprovements | null;
  draft: Partial<QuoteLine>;
  locked: boolean;
  onApply: (patch: Partial<QuoteLine>, confirmation: string) => void;
  onApprove?: (field: string, bc: string, value: string, outcome: string) => void;
  onApproveMany?: (records: Array<{ field: string; bc: string; value: string; outcome: string }>) => void;
  onReject?: (field: string, bc: string, value: string) => void;
  onRestore?: (index: number) => void;
}> = ({ improvements, draft, locked, onApply, onApprove, onApproveMany, onReject, onRestore }) => {
  const [edits, setEdits] = useState<Record<string, string>>({});
  const rejected = draft.erp_extras?.rejections;
  const rejections = Array.isArray(rejected) ? rejected : [];
  if (!improvements && !rejections.length) return null;
  const hint = (key: keyof LineImprovements) => improvements?.[key];
  const row = (
    key: string,
    label: string,
    item: NonNullable<ReturnType<typeof hint>> | undefined,
    patch: Partial<QuoteLine> | null,
    next?: string | null,
  ) => item && {
    key,
    label,
    current: item.current || 'blank',
    next: next ?? item.value,
    source: item.source,
    missing: item.missing,
    outcome: item.outcome || (item.missing ? 'flag' : ''),
    risk: Boolean(item.risk),
    matchType: item.match_type || '',
    flagText: item.missing || item.over_limit ? (item.label || label) : '',
    rejectValue: item.value || '',
    overLimit: Boolean(item.over_limit),
    maxLength: item.max_length || 20,
    patch,
  };
  const items = [
    row('manufacturer', 'Manufacturer', hint('manufacturer'), hint('manufacturer')?.value ? {
      manufacturer: hint('manufacturer')?.value || '',
      erp_extras: { manufacturerCode: hint('manufacturer')?.value },
    } : null, hint('manufacturer')?.value || hint('manufacturer')?.label),
    row('description', 'Description', hint('description'), hint('description')?.value ? {
      description: hint('description')?.value || '',
      erp_extras: { description: hint('description')?.value },
    } : null),
    row('customer_part_number', 'Customer No.', hint('customer_part_number'), hint('customer_part_number')?.value ? {
      customer_part_number: hint('customer_part_number')?.value || '',
      erp_extras: { customerPartNumber: hint('customer_part_number')?.value },
    } : null),
    row('rohs', 'RoHS', hint('rohs'), hint('rohs')?.value ? { erp_extras: { rohsCompliant: hint('rohs')?.value } } : null, hint('rohs')?.label || hint('rohs')?.value),
    row('reach', 'REACH', hint('reach'), hint('reach')?.value ? { erp_extras: { reachCompliance: hint('reach')?.value } } : null, hint('reach')?.label || hint('reach')?.value),
    row('ul_file', 'UL file', hint('ul_file'), hint('ul_file')?.value ? { erp_extras: { ulFileNo: hint('ul_file')?.value } } : null),
    row('ul_ccn', 'UL CCN', hint('ul_ccn'), hint('ul_ccn')?.value ? { erp_extras: { ulCcnNo: hint('ul_ccn')?.value } } : null),
  ].filter(Boolean) as Array<{
    key: string;
    label: string;
    current: string;
    next?: string | null;
    source?: string;
    missing?: boolean;
    outcome: string;
    risk: boolean;
    matchType: string;
    flagText: string;
    rejectValue: string;
    overLimit: boolean;
    maxLength: number;
    patch: Partial<QuoteLine> | null;
  }>;
  if (!items.length && !rejections.length) return null;
  const usable = items.filter((item) => item.patch);
  return (
    <Alert severity="success" sx={{ mt: 1, mb: 1 }}>
      <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
        Suggested improvements after crawl
      </Typography>
      {items.map((item) => {
        const extras = draft.erp_extras || {};
        const extraKey = ({ rohs: 'rohsCompliant', reach: 'reachCompliance', ul_file: 'ulFileNo', ul_ccn: 'ulCcnNo' } as Record<string, string>)[item.key];
        const applied = item.next && (
          (item.key === 'manufacturer' && draft.manufacturer === item.next)
          || (item.key === 'description' && draft.description === item.next)
          || (item.key === 'customer_part_number' && draft.customer_part_number === item.next)
          || (extraKey && String(extras[extraKey] || '') === String(item.rejectValue || item.next))
        ) ? item.next : '';
        const outcome = OUTCOME_LABEL[item.outcome] || '';
        const notes = [
          item.matchType === 'possible' ? 'possible match' : '',
          item.matchType === 'alias' ? 'alias' : '',
          item.risk ? 'higher risk' : '',
        ].filter(Boolean).join(', ');
        const edited = edits[item.key] ?? item.rejectValue;
        const overLimit = item.overLimit && edited.length > item.maxLength;
        const sentence = applied
          ? `${item.label} set to ${applied}`
          : item.outcome === 'flag' || item.missing
            ? item.overLimit
              ? `Flag · ${item.flagText}: ${item.current} → ${edited}`
              : `Flag · ${item.flagText}${item.source ? ` (${item.source})` : ''}`
            : `${outcome ? `${outcome} · ` : ''}${item.label}: ${item.current} → ${item.next}${notes ? ` · ${notes}` : ''}${item.source ? ` (${item.source})` : ''}`;
        const canDecide = Boolean(item.patch) && !item.missing && !applied;
        return (
        <Box key={item.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
          <Typography variant="body2">{sentence}</Typography>
          {item.overLimit && (
            <TextField
              size="small"
              value={edited}
              disabled={locked}
              onChange={(event) => setEdits((current) => ({ ...current, [item.key]: event.target.value }))}
              helperText={`${edited.length} / ${item.maxLength}`}
              error={overLimit}
              sx={{ minWidth: 220 }}
            />
          )}
          {canDecide && onApprove && (
            <Button
              size="small"
              variant="outlined"
              disabled={locked || overLimit}
              sx={rectButtonSx}
              onClick={() => onApprove(item.key, item.current, item.overLimit ? edited : item.rejectValue, item.outcome || 'add')}
            >
              Approve
            </Button>
          )}
          {canDecide && onReject && item.rejectValue && (
            <Button size="small" variant="outlined" color="inherit" disabled={locked} sx={rectButtonSx} onClick={() => onReject(item.key, item.current, item.overLimit ? edited : item.rejectValue)}>
              Reject
            </Button>
          )}
        </Box>
        );
      })}
      {usable.length > 1 && (onApproveMany || onApprove) && (
        <Button
          size="small"
          variant="outlined"
          disabled={locked}
          sx={rectButtonSx}
          onClick={() => {
            const records = usable.flatMap((item) => {
              const edited = edits[item.key] ?? item.rejectValue;
              if (item.overLimit && edited.length > item.maxLength) return [];
              return [{
                field: item.key,
                bc: item.current,
                value: item.overLimit ? edited : item.rejectValue,
                outcome: item.outcome || 'add',
              }];
            });
            if (!records.length) return;
            if (onApproveMany) onApproveMany(records);
            else records.forEach((record) => onApprove?.(record.field, record.bc, record.value, record.outcome));
          }}
        >
          Approve all
        </Button>
      )}
      {rejections.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Rejected for this item
          </Typography>
          {rejections.map((record: { field?: string; bc?: string; value?: string }, index: number) => (
            <Box key={`${record.field}-${index}`} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption">
                {record.field}: {record.bc || 'blank'} → {record.value}
              </Typography>
              {onRestore && (
                <Button size="small" variant="text" disabled={locked} onClick={() => onRestore(index)}>
                  Restore
                </Button>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Alert>
  );
};

interface EnhancementViewProps {
  quote: Quote;
  onQuoteChange?: (quote: Quote) => void;
}

const EnhancementView: React.FC<EnhancementViewProps> = ({ quote }) => {
  const { user, token } = useAuth();
  const [erpTarget, setErpTarget] = useState<authApi.ErpTarget | null>(null);
  const [lines, setLines] = useState<QuoteLine[]>([]);
  const [linesReady, setLinesReady] = useState(false);
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');
  const [originFilter, setOriginFilter] = useState('all');
  const [sortKey, setSortKey] = useState('tag');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [selected, setSelected] = useState<QuoteLine | null>(null);
  const [draft, setDraft] = useState<Partial<QuoteLine>>({});
  const [crawlDraft, setCrawlDraft] = useState('');
  const [popup, setPopup] = useState<'erp' | 'crawl' | 'add' | 'add-erp' | 'match' | 'crawl-prompt' | 'erp-review' | null>(null);
  const [addDraft, setAddDraft] = useState({
    tag: '',
    description: '',
    vendor_item_no: '',
    manufacturer: '',
    customer_part_number: '',
    qty: 1,
    tag_origin: 'Additional',
  });
  const [erpDraft, setErpDraft] = useState({
    erp_number: '',
    tag: '',
    qty: 1,
    tag_origin: 'Additional',
    vendor_item_no: '',
    description: '',
    manufacturer: '',
    customer_part_number: '',
  });
  const [catalogHint, setCatalogHint] = useState('');
  const [catalogMatches, setCatalogMatches] = useState<quoteApi.ErpCatalogMatch[]>([]);
  const [selectedMatch, setSelectedMatch] = useState<quoteApi.ErpCatalogMatch | null>(null);
  const [pendingNew, setPendingNew] = useState<QuoteLine | null>(null);
  const [erpPreview, setErpPreview] = useState<quoteApi.ErpPreview | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [limitEdits, setLimitEdits] = useState<Record<string, string>>({});
  const [decisionQuery, setDecisionQuery] = useState('');
  const [amendResult, setAmendResult] = useState<quoteApi.AmendErpResult | null>(null);
  const [vendorChanges, setVendorChanges] = useState<quoteApi.VendorChange[] | null>(null);
  const [duplicateHint, setDuplicateHint] = useState('');
  const [schematic, setSchematic] = useState<quoteApi.SchematicMeta | null>(null);
  const [schematicPage, setSchematicPage] = useState<number | null>(null);
  const [focusLineId, setFocusLineId] = useState<number | null>(null);
  const locked = quote.status === 'finalised';
  const excelImport = quote.source === 'erp_upload';

  const selectLine = (line: QuoteLine) => {
    setSelected(line);
    setDraft(line);
    setCrawlDraft(line.crawl_json ? JSON.stringify(line.crawl_json, null, 2) : '');
  };

  const load = async () => {
    const data = await quoteApi.listLines(quote.id);
    setLines(data);
    setLinesReady(true);
    setSelected((prev) => {
      const next = prev ? data.find((l) => l.id === prev.id) || null : null;
      if (next) {
        setDraft(next);
        setCrawlDraft(next.crawl_json ? JSON.stringify(next.crawl_json, null, 2) : '');
      }
      return next;
    });
  };

  useEffect(() => {
    setLinesReady(false);
    setLines([]);
    load().catch((err) => setError(err.message || 'Failed to load lines'));
  }, [quote.id]);

  useEffect(() => {
    if (!token) return;
    let cancel = false;
    const loadTarget = () => {
      authApi.getErpTarget(token).then((target) => {
        if (!cancel) setErpTarget(target);
      }).catch(() => {});
    };
    loadTarget();
    window.addEventListener('focus', loadTarget);
    return () => {
      cancel = true;
      window.removeEventListener('focus', loadTarget);
    };
  }, [token]);

  const active = lines.filter((l) => !l.is_deleted);
  const deleted = lines.filter((l) => l.is_deleted);
  const suggestionGroups = useMemo(() => {
    const groups = new Map<string, { key: string; sample: QuoteLine; ids: number[] }>();
    for (const line of active) {
      if (!line.erp_number || line.erp_number === 'NEW ITEM') continue;
      const imp = line.improvements;
      if (!imp || (!imp.description && !imp.manufacturer && !imp.customer_part_number && !imp.rohs && !imp.reach && !imp.ul_file && !imp.ul_ccn)) continue;
      const key = (line.vendor_item_no || line.erp_number || `line-${line.id}`).toString();
      const existing = groups.get(key);
      if (existing) existing.ids.push(line.id);
      else groups.set(key, { key, sample: line, ids: [line.id] });
    }
    return Array.from(groups.values());
  }, [active]);

  const decisions = useMemo(() => {
    const approved: Array<{ line: QuoteLine; record: Record<string, string> }> = [];
    const rejected: Array<{ line: QuoteLine; record: Record<string, string>; index: number }> = [];
    for (const line of active) {
      const extras = line.erp_extras || {};
      const approvals = Array.isArray(extras.approvals) ? extras.approvals : [];
      const rejections = Array.isArray(extras.rejections) ? extras.rejections : [];
      approvals.forEach((record: Record<string, string>) => {
        if (record?.field) approved.push({ line, record });
      });
      rejections.forEach((record: Record<string, string>, index: number) => {
        if (record?.field) rejected.push({ line, record, index });
      });
    }
    return { approved, rejected };
  }, [active]);

  const decisionMatches = (line: QuoteLine, record: Record<string, string>) => {
    const needle = decisionQuery.trim().toLowerCase();
    if (!needle) return true;
    return [line.erp_number, line.vendor_item_no, record.field, record.user, record.at, record.value, record.bc]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
      .includes(needle);
  };

  const filtered = (tab === 1 ? deleted : active).filter((l) => {
    if (originFilter !== 'all' && l.tag_origin !== originFilter) return false;
    if (!search) return true;
    const hay = [l.tag, l.ref, l.erp_number, l.description, l.vendor_item_no, l.customer_part_number, l.manufacturer]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return hay.includes(search.toLowerCase());
  });
  const sortedVisible = sortBy(filtered, sortKey, sortDir);
  const focusIndex = focusLineId == null ? -1 : sortedVisible.findIndex((line) => line.id === focusLineId);
  const visible = focusIndex > 0
    ? [sortedVisible[focusIndex], ...sortedVisible.slice(0, focusIndex), ...sortedVisible.slice(focusIndex + 1)]
    : sortedVisible;

  useEffect(() => {
    if (focusLineId == null) return;
    document.querySelector(`[data-line-id="${focusLineId}"]`)?.scrollIntoView({ block: 'center' });
  }, [focusLineId, lines]);

  const toggleSort = (key: string) => {
    setFocusLineId(null);
    if (sortKey === key) setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const stats = useMemo(
    () => ({
      total: active.length,
      pending: active.filter((l) => l.enrich_status === 'pending' || l.enrich_status === 'stale').length,
      complete: active.filter((l) => l.enrich_status === 'complete').length,
      deleted: deleted.length,
    }),
    [active, deleted]
  );

  const namingOk = !draft.description || NAMING.test(draft.description);

  const handleDelete = async (line: QuoteLine) => {
    await quoteApi.deleteLine(quote.id, line.id);
    await load();
  };

  const handleRestore = async (line: QuoteLine) => {
    await quoteApi.restoreLine(quote.id, line.id);
    await load();
  };

  const saveTagRef = async (line: QuoteLine, value: string) => {
    const next = value.trim().slice(0, 32);
    const current = displayTagRef(line);
    if (next === current) return;
    const keys = new Set([line.erp_number, line.vendor_item_no].filter((item) => item && item !== 'NEW ITEM'));
    if (next && keys.has(next)) {
      setError('Tag/Ref cannot be the ERP PP# or Vendor Item No. Those keys are locked.');
      return;
    }
    setBusy(true);
    try {
      await quoteApi.updateLine(quote.id, line.id, { tag: next || null, ref: next || null } as Partial<QuoteLine>);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update Tag/Ref');
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!selected) return;
    const existingItem = Boolean(selected.erp_number && selected.erp_number !== 'NEW ITEM');
    const tagRef = displayTagRef({ ...selected, ...draft });
    setBusy(true);
    try {
      await quoteApi.updateLine(quote.id, selected.id, {
        description: draft.description,
        customer_part_number: draft.customer_part_number,
        manufacturer: draft.manufacturer,
        qty: draft.qty,
        tag: tagRef || null,
        ref: tagRef || null,
        type_category: draft.type_category,
        connections: draft.connections,
        mounting_method: draft.mounting_method,
        mounting_location: draft.mounting_location,
        ...(existingItem ? {} : { vendor_item_no: draft.vendor_item_no }),
        ...(draft.erp_extras ? { erp_extras: draft.erp_extras } : {}),
      } as Partial<QuoteLine>);
      setNotice('Saved. Matching ERP rows on this quote were updated together.');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCrawlAll = async () => {
    setBusy(true);
    setError('');
    try {
      await quoteApi.crawlAll(quote.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Crawl failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCrawlOne = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await quoteApi.crawlLine(quote.id, selected.id);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Crawl failed');
    } finally {
      setBusy(false);
    }
  };

  const handleErp = async (forceCreate = false) => {
    if (!selected) return;
    setBusy(true);
    setDuplicateHint('');
    try {
      const written = await quoteApi.writeErp(quote.id, selected.id, {
        description: draft.description,
        customerPartNumber: draft.customer_part_number,
        manufacturerCode: draft.manufacturer,
        override_naming: !namingOk,
        force_create: forceCreate,
      });
      const createdNo = written.erp_number && written.erp_number !== 'NEW ITEM' ? written.erp_number : '';
      const applied = written.matching_lines_updated || 0;
      const appliedNote = applied ? ` Applied to ${applied} other line${applied === 1 ? '' : 's'} with the same item.` : '';
      setNotice(
        createdNo
          ? `Created in ERP as ${createdNo}.${appliedNote}`
          : (forceCreate ? `Created in ERP (duplicate override).${appliedNote}` : 'ERP write saved and line refreshed.')
      );
      await load();
    } catch (err: any) {
      const detail = err.response?.data?.detail || err.message || 'ERP write failed';
      if (String(detail).startsWith('Duplicate:')) {
        setDuplicateHint(detail);
      } else {
        setError(detail);
      }
    } finally {
      setBusy(false);
    }
  };

  const openSchematic = async (line: QuoteLine) => {
    try {
      const meta = await quoteApi.getSchematicMeta(quote.id, line.id);
      setSchematic(meta);
      setSchematicPage(meta.pages[0] || null);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'No schematic for this TAG');
    }
  };

  const emptyAddDraft = {
    tag: '',
    description: '',
    vendor_item_no: '',
    manufacturer: '',
    customer_part_number: '',
    qty: 1,
    tag_origin: 'Additional',
  };
  const emptyErpDraft = {
    erp_number: '',
    tag: '',
    qty: 1,
    tag_origin: 'Additional',
    vendor_item_no: '',
    description: '',
    manufacturer: '',
    customer_part_number: '',
  };

  const submitLine = async (fields: Record<string, unknown>) => {
    const line = await quoteApi.addLine(quote.id, fields as Partial<QuoteLine> & { use_catalog?: boolean });
    await load();
    return line;
  };

  const addFromCatalog = async (item: quoteApi.ErpCatalogMatch) => {
    setBusy(true);
    setError('');
    try {
      const line = await submitLine({
        erp_number: item.pp_number || undefined,
        tag: addDraft.tag.trim() || undefined,
        qty: Number(addDraft.qty) || 1,
        tag_origin: addDraft.tag_origin,
        vendor_item_no: item.vendor_item_no || addDraft.vendor_item_no.trim() || undefined,
        description: item.description || undefined,
        manufacturer: item.manufacturer_code || item.manufacturer || undefined,
        customer_part_number: item.customer_part_number || addDraft.customer_part_number.trim() || undefined,
        use_catalog: true,
      });
      setAddDraft(emptyAddDraft);
      setCatalogMatches([]);
      setPopup(null);
      setTab(0);
      setFocusLineId(line.id);
      selectLine(line);
      setNotice('Added from the ERP catalogue. Description and manufacturer came from the catalogue.');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add line');
    } finally {
      setBusy(false);
    }
  };

  const addAsNew = async () => {
    const description = addDraft.description.trim();
    const vendor = addDraft.vendor_item_no.trim();
    setBusy(true);
    setError('');
    try {
      const line = await submitLine({
        tag: addDraft.tag.trim() || undefined,
        description: description || undefined,
        vendor_item_no: vendor || undefined,
        manufacturer: addDraft.manufacturer.trim() || undefined,
        customer_part_number: addDraft.customer_part_number.trim() || undefined,
        qty: Number(addDraft.qty) || 1,
        tag_origin: addDraft.tag_origin,
      });
      setAddDraft(emptyAddDraft);
      setCatalogMatches([]);
      setPendingNew(line);
      setTab(0);
      setFocusLineId(line.id);
      selectLine(line);
      setPopup('crawl-prompt');
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add line');
    } finally {
      setBusy(false);
    }
  };

  const openErpReview = async (line: QuoteLine) => {
    selectLine(line);
    setPopup('erp-review');
    setErpPreview(null);
    setBusy(true);
    setError('');
    try {
      const preview = await quoteApi.previewErp(quote.id, line.id);
      setErpPreview(preview);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Could not prepare the ERP record');
      setPopup('erp');
    } finally {
      setBusy(false);
    }
  };

  const handleAdd = async () => {
    const description = addDraft.description.trim();
    const vendor = addDraft.vendor_item_no.trim();
    if (!description && !vendor) {
      setError('Enter a description or vendor item number.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (vendor) {
        const found = await quoteApi.searchCatalog(vendor);
        const near = found.filter((item) => item.match !== 'exact');
        const exact = found.filter((item) => item.match === 'exact');
        if (found.length > 1 || near.length > 0) {
          setCatalogMatches(found);
          setSelectedMatch(exact[0] || found[0] || null);
          setPopup('match');
          setBusy(false);
          return;
        }
        if (exact.length === 1) {
          setBusy(false);
          await addFromCatalog(exact[0]);
          return;
        }
      }
      setBusy(false);
      await addAsNew();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add line');
      setBusy(false);
    }
  };

  const lookupErpItem = async (pp: string) => {
    if (!pp.trim()) {
      setCatalogHint('');
      return;
    }
    try {
      const item = await quoteApi.lookupCatalog(pp.trim());
      setErpDraft((current) => ({
        ...current,
        erp_number: item.pp_number || current.erp_number,
        vendor_item_no: item.vendor_item_no || '',
        description: item.description || '',
        manufacturer: item.manufacturer || item.manufacturer_code || '',
        customer_part_number: item.customer_part_number || '',
      }));
      setCatalogHint(`Catalog: ${item.pp_number || pp} · ${item.description || 'no description'}`);
    } catch (err: any) {
      setCatalogHint(err.response?.data?.detail || 'Not found in ERP catalog');
    }
  };

  const handleAddErp = async () => {
    if (!erpDraft.erp_number.trim()) {
      setError('Enter an ERP item number.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const line = await quoteApi.addLine(quote.id, {
        erp_number: erpDraft.erp_number.trim(),
        tag: erpDraft.tag.trim() || undefined,
        qty: Number(erpDraft.qty) || 1,
        tag_origin: erpDraft.tag_origin,
        vendor_item_no: erpDraft.vendor_item_no.trim() || undefined,
        description: erpDraft.description.trim() || undefined,
        manufacturer: erpDraft.manufacturer.trim() || undefined,
        customer_part_number: erpDraft.customer_part_number.trim() || undefined,
        use_catalog: true,
      } as Partial<QuoteLine>);
      setErpDraft(emptyErpDraft);
      setCatalogHint('');
      setPopup(null);
      setTab(0);
      setFocusLineId(line.id);
      selectLine(line);
      setNotice('Added from the ERP catalogue. The new line is at the top of the list.');
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to add ERP item');
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (file: File) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await quoteApi.importExcel(quote.id, file);
      const skips = Array.isArray(result.skipped) ? result.skipped as ImportSkip[] : [];
      const skipText = skips.length
        ? ` ${skips.map((item) => `row ${item.row} skipped ${item.reason}`).join('; ')}.`
        : typeof result.skipped === 'number' && result.skipped > 0
          ? ` (${result.skipped} skipped).`
          : '.';
      setNotice(`Imported ${result.added} line${result.added === 1 ? '' : 's'}.${skipText}`);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  };

  const writeExtras = async (line: QuoteLine, extras: Record<string, any>, confirmation: string) => {
    setBusy(true);
    setError('');
    setNotice(confirmation);
    try {
      await quoteApi.updateLine(quote.id, line.id, { erp_extras: extras });
      if (selected?.id === line.id) setDraft((current) => ({ ...current, erp_extras: extras }));
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update suggestion');
    } finally {
      setBusy(false);
    }
  };

  const decideFields = async (
    targets: QuoteLine[],
    kind: 'approvals' | 'rejections',
    records: Array<{ field: string; bc: string; value: string; outcome?: string }>,
  ) => {
    const unique = targets.filter((line, index, all) => all.findIndex((item) => item.id === line.id) === index);
    if (!unique.length || !records.length) return;
    setBusy(true);
    setError('');
    setNotice(kind === 'approvals' ? 'Approved. It stays in the batch until Amend ERP.' : 'Suggestion rejected');
    try {
      for (const line of unique) {
        const extras = { ...(line.erp_extras || {}) };
        const existing = Array.isArray(extras[kind]) ? extras[kind] : [];
        const fields = new Set(records.map((record) => record.field));
        extras[kind] = [
          ...existing.filter((item: { field?: string }) => !fields.has(String(item?.field || ''))),
          ...records.map((record) => ({
            ...record,
            user: user?.username || '',
            at: new Date().toISOString(),
          })),
        ];
        await quoteApi.updateLine(quote.id, line.id, { erp_extras: extras });
        if (selected?.id === line.id) setDraft((current) => ({ ...current, erp_extras: extras }));
      }
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update suggestion');
    } finally {
      setBusy(false);
    }
  };

  const dropDecision = async (line: QuoteLine, kind: 'approvals' | 'rejections', field: string, index?: number) => {
    const extras = { ...(line.erp_extras || {}) };
    const existing = Array.isArray(extras[kind]) ? [...extras[kind]] : [];
    extras[kind] = kind === 'rejections' && index != null
      ? existing.filter((_: unknown, itemIndex: number) => itemIndex !== index)
      : existing.filter((item: { field?: string }) => item?.field !== field);
    await writeExtras(line, extras, kind === 'approvals' ? 'Removed from the approved batch' : 'Suggestion restored');
  };

  const handleErpSync = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await quoteApi.syncErp(quote.id);
      await load();
      if (result.vendor_changes.length) setVendorChanges(result.vendor_changes);
      const tail = result.window?.complete
        ? ''
        : ' The ERP window was cut short, so run ERP SYNC again to continue.';
      const noun = result.lines_updated === 1 ? 'line' : 'lines';
      setNotice(`ERP sync updated ${result.lines_updated} ${noun}.${tail}`);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'ERP sync failed');
    } finally {
      setBusy(false);
    }
  };

  const runAmend = async (continueStale = false) => {
    setBusy(true);
    setError('');
    try {
      const result = await quoteApi.amendErp(quote.id, continueStale);
      setAmendResult(result);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Amend ERP failed');
    } finally {
      setBusy(false);
    }
  };

  const saveExtras = async (extras: Record<string, any>, confirmation: string) => {
    if (!selected) return;
    await writeExtras(selected, extras, confirmation);
  };

  const rejectSuggestion = async (field: string, bc: string, value: string) => {
    if (!selected) return;
    await decideFields([selected], 'rejections', [{ field, bc, value }]);
  };

  const restoreSuggestion = async (index: number) => {
    if (!selected) return;
    await dropDecision(selected, 'rejections', '', index);
  };

  const applySuggestion = async (patch: Partial<QuoteLine>, confirmation: string) => {
    if (!selected) return;
    const nextDraft: Partial<QuoteLine> = {
      ...draft,
      ...patch,
      erp_extras: { ...(draft.erp_extras || {}), ...(patch.erp_extras || {}) },
    };
    setDraft(nextDraft);
    setNotice(confirmation);
    setBusy(true);
    setError('');
    try {
      await quoteApi.updateLine(quote.id, selected.id, {
        ...(patch.manufacturer !== undefined ? { manufacturer: patch.manufacturer } : {}),
        ...(patch.description !== undefined ? { description: patch.description } : {}),
        ...(patch.customer_part_number !== undefined ? { customer_part_number: patch.customer_part_number } : {}),
        ...(patch.erp_extras ? { erp_extras: nextDraft.erp_extras } : {}),
      });
      await load();
      setDraft(nextDraft);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to apply suggestion');
    } finally {
      setBusy(false);
    }
  };

  const saveCrawl = async () => {
    if (!selected) return null;
    const parsed = JSON.parse(crawlDraft);
    const line = await quoteApi.updateLine(quote.id, selected.id, { crawl_json: parsed });
    await load();
    return line;
  };

  const openPopup = (line: QuoteLine, kind: 'erp' | 'crawl') => {
    selectLine(line);
    setPopup(kind);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {notice && (
        <Alert severity="success" sx={{ mb: 1 }} onClose={() => setNotice('')}>
          {notice}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Chip label={linesReady ? `${stats.total} lines` : 'Loading…'} size="small" sx={{ height: 22 }} />
        <Chip label={`${stats.complete} crawled`} size="small" color="success" variant="outlined" sx={{ height: 22 }} />
        <Chip label={`${stats.pending} pending`} size="small" color="warning" variant="outlined" sx={{ height: 22 }} />
        <Chip label={`${stats.deleted} deleted`} size="small" variant="outlined" sx={{ height: 22 }} />
        <Tooltip
          title={erpTarget ? (
            <Box>
              {erpTarget.endpoints.map((row) => (
                <Typography key={row.key} variant="caption" display="block">
                  {row.label}: {row.url}
                </Typography>
              ))}
            </Box>
          ) : 'ERP target'}
        >
          <Chip
            label={erpTarget?.mode === 'live' ? 'ERP Live' : 'ERP Demo'}
            size="small"
            color={erpTarget?.mode === 'live' ? 'warning' : 'default'}
            variant={erpTarget?.mode === 'live' ? 'filled' : 'outlined'}
            sx={{ height: 22 }}
          />
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={locked}
          onClick={() => { setErpDraft(emptyErpDraft); setCatalogHint(''); setPopup('add-erp'); }}
        >
          Add ERP item
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          disabled={locked}
          onClick={() => { setAddDraft(emptyAddDraft); setPopup('add'); }}
        >
          Add new item / search
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="secondary"
          startIcon={<FileDownloadIcon />}
          href={quoteApi.importTemplateUrl()}
        >
          Template
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={locked || busy}
          onClick={handleErpSync}
          sx={{ bgcolor: '#1a2744', color: '#fff', '&:hover': { bgcolor: '#121b30' } }}
        >
          ERP SYNC
        </Button>
        <Button size="small" variant="contained" color="secondary" component="label" disabled={locked || busy} startIcon={<UploadFileIcon />}>
          Import Excel
          <input
            type="file"
            hidden
            accept=".xlsx,.xls"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) await handleImport(file);
            }}
          />
        </Button>
        <Button size="small" variant="contained" color="info" startIcon={<RefreshIcon />} disabled={locked || busy} onClick={handleCrawlAll}>
          Crawl all
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search tag, ERP#, description, vendor…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ maxWidth: 360, flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Origin</InputLabel>
          <Select value={originFilter} label="Origin" onChange={(e) => setOriginFilter(e.target.value)}>
            <MenuItem value="all">All origins</MenuItem>
            <MenuItem value="Valid">Valid</MenuItem>
            <MenuItem value="Additional">Not on scheme</MenuItem>
            <MenuItem value="Untagged">Untagged</MenuItem>
            <MenuItem value="Invalid">Invalid</MenuItem>
          </Select>
        </FormControl>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 32, '& .MuiTab-root': { minHeight: 32, py: 0, px: 1.25, fontSize: 13, textTransform: 'none' } }}
        >
          <Tab label={`All (${linesReady ? active.length : '…'})`} />
          <Tab label={`Deleted (${linesReady ? deleted.length : '…'})`} />
          <Tab label={`Suggestions (${linesReady ? suggestionGroups.length : '…'})`} />
          <Tab label={`Approved (${linesReady ? decisions.approved.length : '…'})`} />
          <Tab label={`Rejected (${linesReady ? decisions.rejected.length : '…'})`} />
        </Tabs>
      </Box>

      {tab === 2 ? (
        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 220px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>ERP / Vendor</TableCell>
                <TableCell>Current description</TableCell>
                <TableCell>Suggestions</TableCell>
                <TableCell>Lines</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestionGroups.map((group) => {
                const imp = group.sample.improvements;
                const fields = (['manufacturer', 'description', 'customer_part_number', 'rohs', 'reach', 'ul_file', 'ul_ccn'] as const)
                  .map((key) => ({ key, hint: imp?.[key] }))
                  .filter((item) => item.hint);
                const groupLines = lines.filter((line) => group.ids.includes(line.id));
                const choiceFor = (key: string, hint: NonNullable<(typeof fields)[number]['hint']>) => {
                  const editKey = `${group.sample.id}:${key}`;
                  const manualDescription = key === 'description' && Boolean(hint.missing);
                  const edited = limitEdits[editKey] ?? (manualDescription ? '' : (hint.value ?? ''));
                  const maxLength = manualDescription ? DESCRIPTION_MAX : (hint.max_length || 20);
                  const overLimit = Boolean(hint.over_limit) && edited.length > maxLength;
                  const descriptionText = edited.trim();
                  const descriptionOk = manualDescription
                    && descriptionText.length > 0
                    && descriptionText.length <= DESCRIPTION_MAX;
                  const canApprove = manualDescription
                    ? descriptionOk
                    : Boolean(hint.value) && !hint.missing && !overLimit;
                  const value = manualDescription
                    ? descriptionText
                    : (hint.over_limit ? edited : String(hint.value || ''));
                  return { editKey, manualDescription, edited, maxLength, overLimit, descriptionOk, canApprove, value };
                };
                const recordsFor = (onlyReady: boolean) => fields.flatMap((item) => {
                  const hint = item.hint!;
                  const choice = choiceFor(item.key, hint);
                  if (onlyReady && !choice.canApprove) return [];
                  if (!choice.value) return [];
                  return [{
                    field: item.key,
                    bc: hint.current || '',
                    value: choice.value,
                    outcome: item.key === 'description' && hint.missing
                      ? (hint.current && hint.current !== 'blank' ? 'amend' : 'add')
                      : (hint.outcome || 'add'),
                  }];
                });
                return (
                  <TableRow key={group.key} hover>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {group.sample.erp_number || 'NEW ITEM'}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {group.sample.vendor_item_no || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>{group.sample.description || '—'}</TableCell>
                    <TableCell>
                      {fields.map((item) => {
                        const hint = item.hint!;
                        const choice = choiceFor(item.key, hint);
                        const fieldName = FIELD_LABEL[item.key] || item.key;
                        const outcome = OUTCOME_LABEL[hint.outcome || ''] || 'Flag';
                        const detail = choice.manualDescription || hint.missing
                          ? `Flag · ${hint.label}`
                          : hint.over_limit
                            ? `Flag · ${hint.label}: ${hint.current || 'blank'} → ${choice.edited}`
                            : `${outcome} · ${hint.current || 'blank'} → ${hint.label || hint.value}`;
                        if (choice.manualDescription) {
                          return (
                            <Box key={item.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                              <Typography variant="body2">{fieldName} · {detail}</Typography>
                              <ManualDescriptionField
                                locked={locked}
                                busy={busy}
                                onChange={(value) => setLimitEdits((current) => ({ ...current, [choice.editKey]: value }))}
                                onApprove={(value) => decideFields(groupLines, 'approvals', [{
                                  field: item.key,
                                  bc: hint.current || '',
                                  value,
                                  outcome: hint.current && hint.current !== 'blank' ? 'amend' : 'add',
                                }])}
                                onReject={() => decideFields(groupLines, 'rejections', [{
                                  field: item.key,
                                  bc: hint.current || '',
                                  value: hint.label || hint.current || '',
                                }])}
                              />
                            </Box>
                          );
                        }
                        return (
                          <Box key={item.key} sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                            <Typography variant="body2">{fieldName} · {detail}</Typography>
                            {hint.over_limit && (
                              <TextField
                                size="small"
                                value={choice.edited}
                                disabled={locked}
                                error={choice.overLimit}
                                helperText={`${choice.edited.length} / ${choice.maxLength}`}
                                inputProps={{ maxLength: choice.maxLength }}
                                onChange={(event) => setLimitEdits((current) => ({ ...current, [choice.editKey]: event.target.value }))}
                                sx={{ minWidth: 220 }}
                              />
                            )}
                            {(choice.canApprove || (hint.value && !hint.missing)) && (
                              <>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={locked || busy || !choice.canApprove}
                                  sx={rectButtonSx}
                                  onClick={() => {
                                    const fresh = choiceFor(item.key, hint);
                                    if (!fresh.canApprove) return;
                                    decideFields(groupLines, 'approvals', [{
                                      field: item.key,
                                      bc: hint.current || '',
                                      value: fresh.value,
                                      outcome: item.key === 'description' && hint.missing
                                        ? (hint.current && hint.current !== 'blank' ? 'amend' : 'add')
                                        : (hint.outcome || 'add'),
                                    }]);
                                  }}
                                >
                                  Approve
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  disabled={locked || busy}
                                  sx={rectButtonSx}
                                  onClick={() => decideFields(groupLines, 'rejections', [{
                                    field: item.key,
                                    bc: hint.current || '',
                                    value: choice.value || hint.label || hint.current || '',
                                  }])}
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          </Box>
                        );
                      })}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>{group.ids.length}</TableCell>
                    <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                      <Button
                        size="small"
                        variant="contained"
                        sx={rectButtonSx}
                        disabled={locked || busy || recordsFor(true).length === 0}
                        onClick={() => {
                          const records = recordsFor(true);
                          if (records.length) decideFields(groupLines, 'approvals', records);
                        }}
                      >
                        Approve all
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {suggestionGroups.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      Crawl all, then review unique suggestions here.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : tab === 3 ? (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button variant="contained" color={erpTarget?.mode === 'live' ? 'warning' : 'primary'} disabled={locked || busy || decisions.approved.length === 0} onClick={() => runAmend(false)}>
              {erpTarget?.mode === 'live' ? 'Amend live ERP' : 'Amend demo ERP'}
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 260px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>Approved against</TableCell>
                  <TableCell>New value</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {decisions.approved.filter((row) => decisionMatches(row.line, row.record)).map((row) => (
                  <TableRow key={`${row.line.id}-${row.record.field}`}>
                    <TableCell>
                      {row.line.erp_number || 'NEW ITEM'}
                      <Typography variant="caption" display="block" color="text.secondary">{row.line.vendor_item_no || '—'}</Typography>
                    </TableCell>
                    <TableCell>{row.record.outcome || 'add'} · {row.record.field}</TableCell>
                    <TableCell>{row.record.bc || 'blank'}</TableCell>
                    <TableCell>{row.record.value}</TableCell>
                    <TableCell>{row.record.user || '—'}</TableCell>
                    <TableCell>{row.record.at ? row.record.at.replace('T', ' ').slice(0, 16) : '—'}</TableCell>
                    <TableCell>
                      <Button size="small" disabled={locked || busy} onClick={() => dropDecision(row.line, 'approvals', row.record.field)}>
                        Remove
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {decisions.approved.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        Approved fields wait here. Amend syncs each item, then writes the batch to {erpTarget?.mode === 'live' ? 'the live ERP' : 'the demo ERP'}.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : tab === 4 ? (
        <Box>
          <TextField
            size="small"
            placeholder="Filter by item, field, user, or date"
            value={decisionQuery}
            onChange={(event) => setDecisionQuery(event.target.value)}
            sx={{ mb: 1, maxWidth: 360 }}
          />
          <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 260px)' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Item</TableCell>
                  <TableCell>Field</TableCell>
                  <TableCell>BC value</TableCell>
                  <TableCell>Rejected value</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {decisions.rejected.filter((row) => decisionMatches(row.line, row.record)).map((row) => (
                  <TableRow key={`${row.line.id}-${row.record.field}-${row.index}`}>
                    <TableCell>
                      {row.line.erp_number || 'NEW ITEM'}
                      <Typography variant="caption" display="block" color="text.secondary">{row.line.vendor_item_no || '—'}</Typography>
                    </TableCell>
                    <TableCell>{row.record.field}</TableCell>
                    <TableCell>{row.record.bc || 'blank'}</TableCell>
                    <TableCell>{row.record.value}</TableCell>
                    <TableCell>{row.record.user || '—'}</TableCell>
                    <TableCell>{row.record.at ? row.record.at.replace('T', ' ').slice(0, 16) : '—'}</TableCell>
                    <TableCell>
                      <Button size="small" disabled={locked || busy} onClick={() => dropDecision(row.line, 'rejections', row.record.field, row.index)}>
                        Restore
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {decisions.rejected.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        Rejected suggestions stay here and are not offered again for the same item.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : (
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 220px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {([
                ['tag', 'Tag / Ref'],
                ['tag_origin', 'Origin'],
                ['erp_number', 'ERP PP#'],
                ['vendor_item_no', 'Vendor item'],
                ['description', 'Description'],
              ] as Array<[string, string]>).map(([key, label]) => (
                <TableCell key={key} sx={tableHeadCell}>
                  <TableSortLabel active={sortKey === key} direction={sortKey === key ? sortDir : 'asc'} onClick={() => toggleSort(key)}>
                    {label}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell sx={tableHeadCell}>Quality</TableCell>
              <TableCell sx={tableHeadCell}>
                <TableSortLabel active={sortKey === 'qty'} direction={sortKey === 'qty' ? sortDir : 'asc'} onClick={() => toggleSort('qty')}>Qty</TableSortLabel>
              </TableCell>
              <TableCell sx={tableHeadCell}>
                <TableSortLabel active={sortKey === 'manufacturer'} direction={sortKey === 'manufacturer' ? sortDir : 'asc'} onClick={() => toggleSort('manufacturer')}>Mfr</TableSortLabel>
              </TableCell>
              <TableCell sx={tableHeadCell}>
                <TableSortLabel active={sortKey === 'enrich_status'} direction={sortKey === 'enrich_status' ? sortDir : 'asc'} onClick={() => toggleSort('enrich_status')}>Enrichment</TableSortLabel>
              </TableCell>
              <TableCell sx={tableHeadCell} align="center">ERP</TableCell>
              <TableCell sx={tableHeadCell} align="center">Crawl</TableCell>
              <TableCell sx={tableHeadCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {visible.map((line) => (
              <TableRow
                key={line.id}
                hover
                data-line-id={line.id}
                selected={line.id === focusLineId}
                sx={line.id === focusLineId ? { bgcolor: 'rgba(72, 187, 120, 0.16)' } : undefined}
              >
                <TableCell sx={{ fontWeight: 600 }}>
                  {excelImport && !locked ? (
                    <TextField
                      size="small"
                      defaultValue={displayTagRef(line)}
                      key={`${line.id}-${displayTagRef(line)}`}
                      disabled={busy}
                      onBlur={(e) => saveTagRef(line, e.target.value)}
                      inputProps={{ maxLength: 32, 'aria-label': 'Tag / Ref' }}
                      placeholder="Tag"
                      sx={{ minWidth: 120, '& .MuiInputBase-input': { fontWeight: 600, py: 0.5 } }}
                    />
                  ) : (
                    displayTagRef(line) || '—'
                  )}
                </TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" color={originColor(line.tag_origin) as any} label={line.tag_origin === 'Additional' ? 'Not on scheme' : line.tag_origin} />
                </TableCell>
                <TableCell sx={{ color: line.erp_number === 'NEW ITEM' ? 'error.main' : undefined, fontWeight: line.erp_number === 'NEW ITEM' ? 600 : 400 }}>
                  {line.erp_number || '—'}
                </TableCell>
                <TableCell>{line.vendor_item_no || '—'}</TableCell>
                <TableCell>{line.description || '—'}</TableCell>
                <TableCell>
                  {(line.quality_flags || []).includes('incomplete_part') && (
                    <Chip size="small" color="warning" variant="outlined" label="Incomplete PN" sx={{ mr: 0.5, height: 20 }} />
                  )}
                  {(line.quality_flags || []).includes('poor_description') && (
                    <Chip size="small" color="warning" variant="outlined" label="Poor desc" sx={{ height: 20 }} />
                  )}
                </TableCell>
                <TableCell>{line.qty}</TableCell>
                <TableCell>{line.manufacturer || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" variant="outlined" color={enrichColor(line.enrich_status) as any} label={enrichLabel(line.enrich_status)} />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="ERP details">
                    <IconButton
                      size="small"
                      color={line.erp_number && line.erp_number !== 'NEW ITEM' ? 'secondary' : 'warning'}
                      onClick={() => openPopup(line, 'erp')}
                    >
                      <Inventory2Icon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell align="center">
                  <Tooltip title={line.crawl_json ? `Crawl ${line.crawl_confidence ?? '—'}%` : 'No crawl data'}>
                    <IconButton
                      size="small"
                      color={line.crawl_json ? 'success' : 'default'}
                      onClick={() => openPopup(line, 'crawl')}
                    >
                      <TravelExploreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {tab === 0 ? (
                    <Tooltip title="Delete">
                      <IconButton size="small" disabled={locked} onClick={() => handleDelete(line)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Restore">
                      <IconButton size="small" color="success" disabled={locked} onClick={() => handleRestore(line)}>
                        <RestoreIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                    {linesReady ? 'No lines in this view.' : 'Loading lines…'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      <Dialog open={popup === 'add'} onClose={() => setPopup(null)} fullWidth maxWidth="sm">
        <DialogTitle>Add new item / search</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="Description"
              required
              value={addDraft.description}
              sx={{ mb: 1 }}
              onChange={(e) => setAddDraft({ ...addDraft, description: e.target.value })}
              helperText="Used only when this part is not already in the ERP catalogue. A catalogue match replaces this text."
            />
            <TextField
              fullWidth
              size="small"
              label="Vendor Item No."
              value={addDraft.vendor_item_no}
              sx={{ mb: 1 }}
              onChange={(e) => setAddDraft({ ...addDraft, vendor_item_no: e.target.value })}
              helperText="Catalogue / manufacturer part number — needed for crawl. New items can be crawled after add."
            />
            <TextField
              fullWidth
              size="small"
              label="Manufacturer"
              value={addDraft.manufacturer}
              sx={{ mb: 1 }}
              onChange={(e) => setAddDraft({ ...addDraft, manufacturer: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Tag"
                value={addDraft.tag}
                sx={{ flex: 1 }}
                onChange={(e) => setAddDraft({ ...addDraft, tag: e.target.value })}
              />
              <TextField
                size="small"
                type="number"
                label="Qty"
                value={addDraft.qty}
                sx={{ width: 100 }}
                onChange={(e) => setAddDraft({ ...addDraft, qty: Number(e.target.value) })}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Customer PN"
              value={addDraft.customer_part_number}
              sx={{ mb: 1 }}
              onChange={(e) => setAddDraft({ ...addDraft, customer_part_number: e.target.value })}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Origin</InputLabel>
              <Select
                value={addDraft.tag_origin}
                label="Origin"
                onChange={(e) => setAddDraft({ ...addDraft, tag_origin: e.target.value })}
              >
                <MenuItem value="Additional">Not on scheme</MenuItem>
                <MenuItem value="Untagged">Untagged</MenuItem>
                <MenuItem value="Valid">Valid</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setPopup(null)}>Cancel</Button>
          <Button
            size="small"
            variant="contained"
            disabled={busy || (!addDraft.description.trim() && !addDraft.vendor_item_no.trim())}
            onClick={handleAdd}
          >
            Add new item
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={popup === 'add-erp'} onClose={() => setPopup(null)} fullWidth maxWidth="sm">
        <DialogTitle>Add ERP item</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              size="small"
              label="ERP item number"
              required
              value={erpDraft.erp_number}
              sx={{ mb: 1 }}
              onChange={(e) => setErpDraft({ ...erpDraft, erp_number: e.target.value })}
              onBlur={(e) => lookupErpItem(e.target.value)}
              helperText="Look up an existing PP#; description is taken from the catalog."
            />
            {catalogHint && (
              <Alert severity={erpDraft.description ? 'info' : 'warning'} sx={{ mb: 1 }}>{catalogHint}</Alert>
            )}
            <TextField fullWidth size="small" label="Description" value={erpDraft.description} sx={{ mb: 1 }} InputProps={{ readOnly: true }} />
            <TextField fullWidth size="small" label="Vendor Item No." value={erpDraft.vendor_item_no} sx={{ mb: 1 }} InputProps={{ readOnly: true }} />
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                label="Tag"
                value={erpDraft.tag}
                sx={{ flex: 1 }}
                onChange={(e) => setErpDraft({ ...erpDraft, tag: e.target.value })}
              />
              <TextField
                size="small"
                type="number"
                label="Qty"
                value={erpDraft.qty}
                sx={{ width: 100 }}
                onChange={(e) => setErpDraft({ ...erpDraft, qty: Number(e.target.value) })}
              />
            </Box>
            <FormControl fullWidth size="small">
              <InputLabel>Origin</InputLabel>
              <Select
                value={erpDraft.tag_origin}
                label="Origin"
                onChange={(e) => setErpDraft({ ...erpDraft, tag_origin: String(e.target.value) })}
              >
                <MenuItem value="Additional">Not on scheme</MenuItem>
                <MenuItem value="Untagged">Untagged</MenuItem>
                <MenuItem value="Valid">Valid</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setPopup(null)}>Cancel</Button>
          <Button size="small" variant="contained" disabled={busy || !erpDraft.erp_number.trim()} onClick={handleAddErp}>
            Add ERP item
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={popup === 'erp'} onClose={() => setPopup(null)} fullWidth maxWidth="sm">
        <DialogTitle>
          ERP · {(selected && (displayTagRef(selected) || selected.vendor_item_no)) || 'Line'}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ pt: 1 }}>
              {excelImport && (
                <TextField
                  fullWidth
                  size="small"
                  label="Tag / Ref"
                  value={displayTagRef({ ...selected, ...draft })}
                  sx={{ mb: 1 }}
                  disabled={locked}
                  inputProps={{ maxLength: 32 }}
                  onChange={(e) => setDraft({ ...draft, tag: e.target.value.slice(0, 32), ref: e.target.value.slice(0, 32) })}
                  helperText="Schematic tag only. ERP PP# and Vendor Item No. are locked keys."
                />
              )}
              <TextField fullWidth size="small" label="Description" value={draft.description || ''} multiline rows={2} sx={{ mb: 1 }} disabled={locked} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
              {!namingOk && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                  Description does not match TYPE: DETAILS naming. ERP create will require override.
                </Alert>
              )}
              <TextField
                fullWidth
                size="small"
                label="ERP PP#"
                value={selected.erp_number || ''}
                sx={{ mb: 1 }}
                InputProps={{ readOnly: true }}
                disabled
                helperText="Primary key — cannot be edited"
              />
              <TextField
                fullWidth
                size="small"
                label="Vendor Item No."
                value={draft.vendor_item_no || ''}
                sx={{ mb: 1 }}
                disabled={locked || Boolean(selected.erp_number && selected.erp_number !== 'NEW ITEM')}
                onChange={(e) => setDraft({ ...draft, vendor_item_no: e.target.value })}
                helperText={
                  selected.erp_number && selected.erp_number !== 'NEW ITEM'
                    ? 'Locked on existing ERP items — changing it would corrupt the catalog match'
                    : 'Required to create a new ERP item. Changing this marks crawl stale'
                }
              />
              <TextField fullWidth size="small" label="Customer PN" value={draft.customer_part_number || ''} sx={{ mb: 1 }} disabled={locked} onChange={(e) => setDraft({ ...draft, customer_part_number: e.target.value })} />
              <TextField fullWidth size="small" label="Manufacturer" value={draft.manufacturer || ''} sx={{ mb: 1 }} disabled={locked} onChange={(e) => setDraft({ ...draft, manufacturer: e.target.value })} />
              <TextField fullWidth size="small" type="number" label="Qty" value={draft.qty ?? 1} disabled={locked} onChange={(e) => setDraft({ ...draft, qty: Number(e.target.value) })} />
              <ImprovementPanel
                improvements={selected.improvements}
                draft={draft}
                locked={locked}
                onApply={applySuggestion}
                onApprove={(field, bc, value, outcome) => decideFields([selected], 'approvals', [{ field, bc, value, outcome }])}
                onApproveMany={(records) => decideFields([selected], 'approvals', records)}
                onReject={rejectSuggestion}
                onRestore={restoreSuggestion}
              />
              {selected.erp_catalog && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Current ERP item: {selected.erp_catalog.pp_number || '—'} · {selected.erp_catalog.manufacturer || selected.erp_catalog.manufacturer_code || '—'}
                  {selected.erp_catalog.customer_part_number ? ` · Customer PN ${selected.erp_catalog.customer_part_number}` : ''}
                  {selected.erp_catalog.description ? ` · ${selected.erp_catalog.description}` : ''}
                </Alert>
              )}
              {duplicateHint && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {duplicateHint}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => selected && openSchematic(selected)} disabled={!selected?.tag}>
            Schematic
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setPopup(null)}>Close</Button>
          <Button size="small" variant="contained" disabled={locked || busy} onClick={handleSave}>Save</Button>
          {duplicateHint ? (
            <Button size="small" variant="contained" color="warning" disabled={locked || busy} onClick={() => handleErp(true)}>
              Create anyway
            </Button>
          ) : selected?.erp_number && selected.erp_number !== 'NEW ITEM' ? (
            <Button size="small" variant="contained" color="secondary" disabled={locked || busy} onClick={() => handleErp(false)}>
              Update ERP
            </Button>
          ) : (
            <Button size="small" variant="contained" color="secondary" disabled={locked || busy || !selected} onClick={() => selected && openErpReview(selected)}>
              Review & create
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={popup === 'crawl'} onClose={() => setPopup(null)} fullWidth maxWidth="md">
        <DialogTitle>
          Crawl · {selected?.vendor_item_no || selected?.tag || 'Line'}
          {selected?.crawl_confidence != null ? ` · ${selected.crawl_confidence}%` : ''}
        </DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Status {selected.enrich_status}
                {selected.manufacturer ? ` · crawl manufacturer ${selected.manufacturer}` : ''}
              </Typography>
              {selected.erp_catalog && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  Current ERP item: {selected.erp_catalog.pp_number || '—'} / {selected.erp_catalog.vendor_item_no || '—'}
                  {' · '}{selected.erp_catalog.manufacturer || selected.erp_catalog.manufacturer_code || 'no manufacturer'}
                  {selected.erp_catalog.customer_part_number ? ` · Customer PN ${selected.erp_catalog.customer_part_number}` : ''}
                  {selected.erp_catalog.rohs ? ` · RoHS ${selected.erp_catalog.rohs}` : ''}
                  {selected.erp_catalog.reach ? ` · REACH ${selected.erp_catalog.reach}` : ''}
                  {selected.erp_catalog.ul_file ? ` · UL file ${selected.erp_catalog.ul_file}` : ''}
                  {selected.erp_catalog.ul_ccn ? ` · UL CCN ${selected.erp_catalog.ul_ccn}` : ''}
                </Alert>
              )}
              <ImprovementPanel
                improvements={selected.improvements}
                draft={draft}
                locked={locked}
                onApply={applySuggestion}
                onApprove={(field, bc, value, outcome) => decideFields([selected], 'approvals', [{ field, bc, value, outcome }])}
                onApproveMany={(records) => decideFields([selected], 'approvals', records)}
                onReject={rejectSuggestion}
                onRestore={restoreSuggestion}
              />
              {selected.crawl_json && !Object.keys(selected.improvements || {}).length && (
                <Alert severity="info" sx={{ mb: 1 }}>
                  No suggested changes. The crawl matches this line, including compliance where the ERP record has the same values.
                </Alert>
              )}
              <TextField
                fullWidth
                size="small"
                multiline
                minRows={16}
                value={crawlDraft}
                disabled={locked}
                onChange={(e) => setCrawlDraft(e.target.value)}
                placeholder="No crawl data yet"
                sx={{ mb: 1, fontFamily: 'monospace' }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button size="small" onClick={() => selected && openSchematic(selected)} disabled={!selected?.tag} sx={rectButtonSx}>
            Schematic
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" onClick={() => setPopup(null)} sx={rectButtonSx}>Close</Button>
          <Button size="small" variant="contained" color="info" disabled={locked || busy} onClick={handleCrawlOne} sx={rectButtonSx}>
            Re-crawl
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={locked || busy || !crawlDraft}
            sx={rectButtonSx}
            onClick={async () => {
              try {
                await saveCrawl();
                setNotice('Crawl saved.');
                setPopup(null);
              } catch (err: any) {
                setError(err.message || 'Invalid crawl JSON');
              }
            }}
          >
            Save crawl JSON
          </Button>
          <Button
            size="small"
            variant="contained"
            color="secondary"
            disabled={locked || busy || !selected}
            sx={rectButtonSx}
            onClick={async () => {
              if (!selected) return;
              try {
                const line = crawlDraft ? await saveCrawl() : selected;
                setNotice('Crawl saved.');
                if (line) openPopup(line, 'erp');
              } catch (err: any) {
                setError(err.message || 'Invalid crawl JSON');
              }
            }}
          >
            Open ERP record
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={popup === 'match'} onClose={() => !busy && setPopup('add')} fullWidth maxWidth="lg">
        <DialogTitle>ERP catalogue matches</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Search term "{addDraft.vendor_item_no}" · {catalogMatches.length} possible match{catalogMatches.length === 1 ? '' : 'es'} found in the ERP catalogue
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ERP PP#</TableCell>
                <TableCell>Vendor item</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Description 2</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Match</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {catalogMatches.map((item) => {
                const selected = selectedMatch?.pp_number === item.pp_number && selectedMatch?.vendor_item_no === item.vendor_item_no;
                return (
                  <TableRow
                    key={`${item.pp_number}-${item.vendor_item_no}`}
                    hover
                    selected={selected}
                    onClick={() => setSelectedMatch(item)}
                    sx={{ cursor: 'pointer', bgcolor: item.match === 'exact' ? 'rgba(237, 137, 54, 0.08)' : undefined }}
                  >
                    <TableCell>{item.pp_number || '—'}</TableCell>
                    <TableCell>{item.vendor_item_no || '—'}</TableCell>
                    <TableCell>{item.description || '—'}</TableCell>
                    <TableCell>{item.description_2 || '—'}</TableCell>
                    <TableCell>{item.manufacturer_code || item.manufacturer || '—'}</TableCell>
                    <TableCell>
                      <Chip size="small" label={item.match === 'exact' ? `Exact · ${item.score}%` : `Near · ${item.score}%`} color={item.match === 'exact' ? 'success' : 'warning'} variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        disabled={busy}
                        sx={rectButtonSx}
                        onClick={(event) => {
                          event.stopPropagation();
                          addFromCatalog(item);
                        }}
                      >
                        Use this
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <Alert severity="info" sx={{ mt: 2 }}>
            Description, description 2 and manufacturer are taken from the ERP catalogue record, not from the text typed into the search box.
            If none of these is correct, choose Create as new part.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button size="small" onClick={() => setPopup('add')} disabled={busy} sx={rectButtonSx}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="contained" color="success" onClick={addAsNew} disabled={busy} sx={rectButtonSx}>
            Create as new part
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={busy || !selectedMatch}
            sx={rectButtonSx}
            onClick={() => selectedMatch && addFromCatalog(selectedMatch)}
          >
            Use selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={popup === 'crawl-prompt'} onClose={() => { setPendingNew(null); setPopup(null); }} fullWidth maxWidth="sm">
        <DialogTitle>New item added</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ pt: 1 }}>
            {pendingNew?.vendor_item_no || 'This line'} has no crawl data yet. Crawl it and review the ERP record before creating the part. You can skip and do this later.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button size="small" disabled={busy} onClick={() => { setPendingNew(null); setPopup(null); }}>Skip</Button>
          <Button
            size="small"
            variant="contained"
            disabled={busy || !pendingNew}
            onClick={async () => {
              if (!pendingNew) return;
              setBusy(true);
              setError('');
              try {
                const line = await quoteApi.crawlLine(quote.id, pendingNew.id);
                setPendingNew(null);
                await load();
                await openErpReview(line);
              } catch (err: any) {
                setError(err.response?.data?.detail || err.message || 'Crawl failed');
              } finally {
                setBusy(false);
              }
            }}
          >
            Crawl and review
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={popup === 'erp-review'} onClose={() => !busy && setPopup(null)} fullWidth maxWidth="md">
        <DialogTitle>Review item before creating in ERP</DialogTitle>
        <DialogContent>
          {!erpPreview && <Typography variant="body2" sx={{ py: 2 }}>Preparing the ERP record…</Typography>}
          {erpPreview && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {erpPreview.vendor_item_no || 'New item'}
                {erpPreview.crawl_confidence != null ? ` · crawl ${erpPreview.crawl_confidence}% complete` : ''}
                {' · nothing is written until you press Create in ERP · '}
                {erpTarget?.mode === 'live' ? 'this creates the part on the live ERP' : 'this creates the part on the demo ERP'}
              </Typography>
              {erpPreview.warnings.map((warning) => (
                <Alert key={warning} severity="warning" sx={{ mb: 1 }}>{warning}</Alert>
              ))}
              {createProblems(erpPreview).map((problem) => (
                <Alert key={problem} severity="error" sx={{ mb: 1 }}>{problem}</Alert>
              ))}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <TextField size="small" label="Vendor item no." value={erpPreview.vendor_item_no} inputProps={{ maxLength: 50 }} onChange={(e) => setErpPreview({ ...erpPreview, vendor_item_no: e.target.value })} helperText={`${(erpPreview.vendor_item_no || '').trim().length} / 50`} />
                <Autocomplete
                  size="small"
                  options={erpPreview.manufacturers}
                  getOptionLabel={optionLabel}
                  value={erpPreview.manufacturers.find((row) => row.code === erpPreview.manufacturerCode) || null}
                  onChange={(_event, row) => setErpPreview({ ...erpPreview, manufacturerCode: row?.code || '' })}
                  renderInput={(params) => <TextField {...params} label="Manufacturer (ERP list)" helperText="Must be an ERP manufacturer code." />}
                />
                <TextField size="small" label="Description" value={erpPreview.description} inputProps={{ maxLength: 50 }} onChange={(e) => setErpPreview({ ...erpPreview, description: e.target.value })} helperText={`${(erpPreview.description || '').trim().length} / 50. Prefer TYPE: DETAILS.`} />
                <TextField size="small" label="Description 2" value={erpPreview.description2} inputProps={{ maxLength: 50 }} onChange={(e) => setErpPreview({ ...erpPreview, description2: e.target.value })} helperText={`${(erpPreview.description2 || '').trim().length} / 50`} />
                <FormControl size="small">
                  <InputLabel>Replenishment system</InputLabel>
                  <Select label="Replenishment system" value={erpPreview.replenishmentSystem || 'Purchase'} onChange={(e) => setErpPreview({ ...erpPreview, replenishmentSystem: String(e.target.value) })}>
                    {REPLENISHMENT_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Base unit of measure</InputLabel>
                  <Select label="Base unit of measure" value={erpPreview.baseUnitOfMeasureCode || 'EA'} onChange={(e) => setErpPreview({ ...erpPreview, baseUnitOfMeasureCode: String(e.target.value) })}>
                    {UNIT_OPTIONS.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField size="small" type="number" label="Unit cost" value={erpPreview.unitCost ?? 0} onChange={(e) => setErpPreview({ ...erpPreview, unitCost: Number(e.target.value) })} helperText="Required. A number, zero or greater." />
                <TextField size="small" label="Customer part no." value={erpPreview.customerPartNumber || ''} inputProps={{ maxLength: 20 }} onChange={(e) => setErpPreview({ ...erpPreview, customerPartNumber: e.target.value })} helperText={`${(erpPreview.customerPartNumber || '').trim().length} / 20`} />
                <Autocomplete
                  size="small"
                  options={erpPreview.costCenters || []}
                  getOptionLabel={optionLabel}
                  value={(erpPreview.costCenters || []).find((row) => row.code === erpPreview.costCenter) || null}
                  onChange={(_event, row) => setErpPreview({ ...erpPreview, costCenter: row?.code || '' })}
                  renderInput={(params) => <TextField {...params} label="Cost centre" helperText="Must be a cost centre from ERP." />}
                />
                <TextField size="small" type="number" label="Qty" value={erpPreview.qty ?? 1} onChange={(e) => setErpPreview({ ...erpPreview, qty: Number(e.target.value) })} helperText="Quote quantity. Not sent as an ERP item field." />
                <FormControl size="small">
                  <InputLabel>RoHS compliant</InputLabel>
                  <Select label="RoHS compliant" value={erpPreview.rohsCompliant || 'Unknown'} onChange={(e) => setErpPreview({ ...erpPreview, rohsCompliant: String(e.target.value) })}>
                    {COMPLIANCE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>REACH compliant</InputLabel>
                  <Select label="REACH compliant" value={erpPreview.reachCompliance || 'Unknown'} onChange={(e) => setErpPreview({ ...erpPreview, reachCompliance: String(e.target.value) })}>
                    {COMPLIANCE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small">
                  <InputLabel>Conflict minerals</InputLabel>
                  <Select label="Conflict minerals" value={erpPreview.conflictMineralsStatus || 'Unknown'} onChange={(e) => setErpPreview({ ...erpPreview, conflictMineralsStatus: String(e.target.value) })}>
                    {COMPLIANCE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </FormControl>
                <Autocomplete
                  size="small"
                  options={erpPreview.itemCategories || []}
                  getOptionLabel={optionLabel}
                  value={(erpPreview.itemCategories || []).find((row) => row.code === erpPreview.itemCategoryCode) || null}
                  onChange={(_event, row) => setErpPreview({ ...erpPreview, itemCategoryCode: row?.code || '' })}
                  renderInput={(params) => <TextField {...params} label="Item category" helperText="From the ERP item category list." />}
                />
                <Autocomplete
                  size="small"
                  options={erpPreview.commodities || []}
                  getOptionLabel={optionLabel}
                  value={(erpPreview.commodities || []).find((row) => row.code === erpPreview.commodityCode) || null}
                  onChange={(_event, row) => setErpPreview({ ...erpPreview, commodityCode: row?.code || '' })}
                  renderInput={(params) => <TextField {...params} label="Commodity code" helperText="From the ERP commodity list." />}
                />
                <Autocomplete
                  size="small"
                  options={erpPreview.countries || []}
                  getOptionLabel={(row) => countryLabel(row.code)}
                  value={(erpPreview.countries || []).find((row) => row.code === erpPreview.countryOfOriginCode) || null}
                  onChange={(_event, row) => setErpPreview({ ...erpPreview, countryOfOriginCode: row?.code || '' })}
                  renderInput={(params) => <TextField {...params} label="Country of origin" helperText="ISO-3166 alpha-2, such as GB." />}
                />
                <TextField size="small" label="UKCA / CE" value={erpPreview.ukcaCe || ''} InputProps={{ readOnly: true }} helperText="Shown from the crawl. Not a separate ERP field." />
                <TextField size="small" label="UL file no." value={erpPreview.ulFileNo} inputProps={{ maxLength: 10 }} placeholder={erpPreview.ulMissing ? 'not found — enter manually' : ''} onChange={(e) => setErpPreview({ ...erpPreview, ulFileNo: e.target.value, ulMissing: !e.target.value.trim() })} helperText={erpPreview.ulNote || `${(erpPreview.ulFileNo || '').trim().length} / 10. Leave blank if unknown.`} />
                <TextField size="small" label="UL category no." value={erpPreview.ulCcnNo} inputProps={{ maxLength: 12 }} placeholder={erpPreview.ulMissing ? 'not found — enter manually' : ''} onChange={(e) => setErpPreview({ ...erpPreview, ulCcnNo: e.target.value })} helperText={`${(erpPreview.ulCcnNo || '').trim().length} / 12`} />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" disabled={busy} onClick={() => setPopup(null)} sx={rectButtonSx}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          <Button size="small" disabled={busy || !selected} onClick={() => setPopup('crawl')} sx={{ ...rectButtonSx, bgcolor: 'primary.dark', color: 'common.white', '&:hover': { bgcolor: 'primary.dark' } }}>Back to crawl</Button>
          <Button
            size="small"
            variant="contained"
            sx={rectButtonSx}
            disabled={busy || !erpPreview || createProblems(erpPreview).length > 0}
            onClick={async () => {
              if (!selected || !erpPreview || createProblems(erpPreview).length) return;
              setBusy(true);
              setError('');
              try {
                if (erpPreview.qty != null) {
                  await quoteApi.updateLine(quote.id, selected.id, { qty: Number(erpPreview.qty) || 1 });
                }
                const written = await quoteApi.writeErp(quote.id, selected.id, {
                  description: erpPreview.description,
                  description2: erpPreview.description2,
                  customerPartNumber: erpPreview.customerPartNumber,
                  replenishmentSystem: erpPreview.replenishmentSystem,
                  unitCost: erpPreview.unitCost,
                  manufacturerCode: erpPreview.manufacturerCode,
                  itemCategoryCode: erpPreview.itemCategoryCode,
                  commodityCode: erpPreview.commodityCode,
                  conflictMineralsStatus: erpPreview.conflictMineralsStatus,
                  countryOfOriginCode: erpPreview.countryOfOriginCode,
                  rohsCompliant: erpPreview.rohsCompliant,
                  reachCompliance: erpPreview.reachCompliance,
                  ulFileNo: erpPreview.ulFileNo,
                  ulCcnNo: erpPreview.ulCcnNo,
                  costCenter: erpPreview.costCenter,
                  externalRef: erpPreview.vendor_item_no,
                  baseUnitOfMeasureCode: erpPreview.baseUnitOfMeasureCode,
                  override_naming: !NAMING.test((erpPreview.description || '').trim()),
                });
                const applied = written.matching_lines_updated || 0;
                const appliedNote = applied ? ` Applied to ${applied} other line${applied === 1 ? '' : 's'} with the same item.` : '';
                setNotice(written.erp_number && written.erp_number !== 'NEW ITEM'
                  ? `Created in ERP as ${written.erp_number}.${appliedNote}`
                  : `Created in ERP after review.${appliedNote}`);
                setPopup(null);
                setErpPreview(null);
                await load();
              } catch (err: any) {
                const detail = err.response?.data?.detail || err.message || 'ERP create failed';
                if (String(detail).startsWith('Duplicate:')) setDuplicateHint(detail);
                setError(detail);
              } finally {
                setBusy(false);
              }
            }}
          >
            {erpTarget?.mode === 'live' ? 'Create in live ERP' : 'Create in demo ERP'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(schematic)} onClose={() => setSchematic(null)} fullWidth maxWidth="lg">
        <DialogTitle>Schematic · {schematic?.tag || 'TAG'}</DialogTitle>
        <DialogContent>
          {schematic && selected && (
            <SchematicViewer
              quoteId={quote.id}
              lineId={selected.id}
              meta={schematic}
              page={schematicPage}
              onPageChange={setSchematicPage}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setSchematic(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(vendorChanges)} onClose={() => setVendorChanges(null)} maxWidth="md" fullWidth>
        <DialogTitle>Vendor number changed</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            ERP SYNC updated the lines. These vendor details changed.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tag</TableCell>
                <TableCell>ERP No.</TableCell>
                <TableCell>Previous vendor item</TableCell>
                <TableCell>Vendor item</TableCell>
                <TableCell>Previous vendor no.</TableCell>
                <TableCell>Vendor no.</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(vendorChanges || []).map((row) => (
                <TableRow key={row.line_id}>
                  <TableCell>{row.tag}</TableCell>
                  <TableCell>{row.erp_number}</TableCell>
                  <TableCell>{row.previous_vendor_item}</TableCell>
                  <TableCell>{row.vendor_item}</TableCell>
                  <TableCell>{row.previous_vendor_no}</TableCell>
                  <TableCell>{row.vendor_no}</TableCell>
                  <TableCell>{row.description}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setVendorChanges(null)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={Boolean(amendResult)} onClose={() => setAmendResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{erpTarget?.mode === 'live' ? 'Amend live ERP' : 'Amend demo ERP'}</DialogTitle>
        <DialogContent>
          {amendResult && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
              {amendResult.written.length > 0 && (
                <Alert severity="success">
                  {amendResult.written.map((row) => `${row.item}: ${row.fields.join(', ')}`).join(' · ')}
                </Alert>
              )}
              {amendResult.stale.length > 0 && (
                <Alert severity="warning">
                  These fields changed in BC after approval.
                  {amendResult.stale.map((row) => (
                    <Typography key={row.line_id} variant="body2">
                      {row.item}: {row.changes.map((change) => `${change.field} was ${change.approved_against}, now ${change.now}`).join('; ')}
                    </Typography>
                  ))}
                </Alert>
              )}
              {amendResult.blocked.length > 0 && (
                <Alert severity="error">
                  {amendResult.blocked.map((row) => `${row.item}: ${row.reason}`).join(' ')}
                </Alert>
              )}
              {amendResult.errors.length > 0 && (
                <Alert severity="error">
                  {amendResult.errors.map((row) => `${row.item}: ${row.detail}`).join(' ')}
                </Alert>
              )}
              {amendResult.written.length === 0 && amendResult.stale.length === 0 && amendResult.blocked.length === 0 && amendResult.errors.length === 0 && (
                <Typography variant="body2">Nothing in the approved batch.</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {amendResult && amendResult.stale.length > 0 && (
            <Button onClick={() => runAmend(true)} disabled={busy}>Continue and write</Button>
          )}
          <Button onClick={() => setAmendResult(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancementView;
